import axios from 'axios';
import { webpack } from '@dr.pogodin/react-utils';

import type { GetVimeoSourcesResT, GetVimeoVideoDataResT } from 'server/controllers/video';

import type * as VimeoServiceM from 'server/services/vimeo/getVimeoVideoData';
import { P_API_VIMEO_SOURCES, P_API_VIMEO_VIDEO, getVimeoVideoId } from 'utils';

const ss = webpack.requireWeak<typeof VimeoServiceM>('server/services/vimeo/getVimeoVideoData', import.meta.dirname);

export async function getVimeoVideoData(
  id: string,
): Promise<GetVimeoVideoDataResT> {
  if (ss) {
    const data = await ss.getVimeoVideoData(id);
    return { name: data.name, size: data.files_size.total_size };
  }

  const res = await axios<GetVimeoVideoDataResT>(`${P_API_VIMEO_VIDEO}/${id}`);

  return res.data;
}

/**
 * Extracts the numeric Vimeo video id from a Vimeo URL ('' if not a match).
 * Reuses the codebase parser, which handles both `vimeo.com/<id>` and
 * `player.vimeo.com/video/<id>` forms.
 */
export function parseVimeoId(url: string): string {
  return getVimeoVideoId(url, true) ?? '';
}

type LinksT = {
  hls?: null | string;
  progressive?: Array<{ height?: null | number; link: string }>;
};

/**
 * Chooses the best source URL for a native <video> from the Vimeo links.
 * Prefers the highest progressive MP4 up to 1080p (MP4 plays in every browser,
 * incl. iOS); HLS is only the fallback. NOTE: do not trust
 * canPlayType('application/vnd.apple.mpegurl') — desktop Chrome returns a
 * misleading "maybe" but cannot actually decode HLS.
 */
export function pickSource(links: LinksT): null | string {
  const sorted = [...links.progressive ?? []]
    .sort((a, b) => (b.height ?? 0) - (a.height ?? 0));
  const best = sorted.find((p) => (p.height ?? 0) <= 1080) ?? sorted[0];
  // HLS.JS PLUG-IN POINT: for adaptive bitrate, return links.hls here and play
  // it via hls.js on non-Safari engines (iOS/Safari play HLS natively).
  return best?.link ?? links.hls ?? null;
}

/**
 * Fetches a Vimeo video's streaming links from our API. Client-only: signed
 * links must never be fetched/SSR-ed into HTML.
 */
export async function fetchVimeoLinks(
  url: string,
): Promise<GetVimeoSourcesResT> {
  const id = parseVimeoId(url);
  if (!id) throw Error(`Not a Vimeo URL: ${url}`);
  const res = await axios.get<GetVimeoSourcesResT>(P_API_VIMEO_SOURCES, {
    params: { id },
    withCredentials: true,
  });
  return res.data;
}
